/**
 * 🧮 수량 세기 계산기 — 누르는 규칙 · 계산 · 보이는 모양 (v2.207.0 · 2026-09-28)
 *
 * 사용자 요청(주요재고 확인 화면): 「박스단위로 숫자가 세기 힘든게 있어서 요 메뉴 안에 계산기가
 *   들어가면 편할것 같아. 사칙연산 가능하도록 계산기 기능을 넣어줘」
 *
 * ⭐계산은 **금액 칸의 `=` 수식과 같은 평가기**(`lib/evalAmount.ts`)를 부른다 — 곱하기·나누기를 먼저
 *   한다(7 + 4 × 20 = 87 · 폰 계산기와 같다). 평가기를 따로 두면 두 곳의 답이 갈린다.
 * ⭐화면 없이 도는 순수 함수만 둔다 — 그물(`npm run check:calc`)이 그대로 부른다.
 *
 * 누르는 규칙 (폰 계산기와 같게)
 *   · 기호를 잇달아 누르면 **마지막 것으로 바뀐다**(4 + × → 4 ×)
 *   · [=] 뒤에 숫자를 누르면 새 계산 · 기호를 누르면 그 답에 이어서
 *   · 맨 앞 0 은 겹치지 않는다(007 → 7) · 소수점은 한 수에 하나 · 한 수는 9자리까지
 *   · 식 끝의 기호는 계산할 때 없는 것으로 본다 — 「4 × 20 +」 에서도 80 을 미리 보여 준다
 *   · 빈 식에는 기호부터 못 적는다(개수를 세는 계산기라 음수로 시작할 일이 없다).
 *     음수는 답으로만 생긴다(5 − 8 = −3) — 그 답에 이어서 계산할 수 있게 맨 앞 부호는 받는다.
 */
import { evalAmount } from './evalAmount';

export type CalcOp = '+' | '-' | '*' | '/';
export type CalcKey =
  | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'
  | '.' | CalcOp | '=' | 'C' | 'back';

export interface CalcState {
  /** 식 — 기호는 + - * / 로 적는다(보일 때만 + − × ÷). 맨 앞 - 는 음수 부호 */
  expr: string;
  /** 방금 [=]을 눌러 답이 식 자리에 들어간 상태 */
  done: boolean;
}

export const CALC_EMPTY: CalcState = { expr: '', done: false };

/** 한 수의 자릿수 상한 */
export const CALC_MAX_DIGITS = 9;
/** 식 길이 상한 */
export const CALC_MAX_LEN = 60;
/** 답 크기 상한 — 넘으면 「수가 너무 큽니다」 */
export const CALC_MAX_ABS = 1e12;
/** 수량 칸에 넘길 수 있는 가장 큰 수 — 화면 수량 칸이 6자리(QTY_RE)라 999,999 */
export const CALC_QTY_MAX = 999_999;

const OPS = '+-*/';

/** i 자리 글자가 연산 기호인가 — 맨 앞(0번) - 는 기호가 아니라 음수 부호다 */
function isOpAt(expr: string, i: number): boolean {
  return i > 0 && OPS.includes(expr.charAt(i));
}

function endsWithOp(expr: string): boolean {
  return expr.length > 0 && isOpAt(expr, expr.length - 1);
}

/** 식 끝의 수(마지막 기호 뒤) — 음수 부호가 붙어 있으면 그것까지 */
function lastNumber(expr: string): string {
  let i = expr.length - 1;
  while (i >= 0 && !isOpAt(expr, i)) i--;
  return expr.slice(i + 1);
}

function pressDigit(s: CalcState, d: string): CalcState {
  if (s.done) return { expr: d, done: false };
  const bare = lastNumber(s.expr).replace('-', '');
  // 맨 앞 0 은 겹치지 않게 바꾼다 (0 → 7 · 5 + 0 → 5 + 7)
  if (bare === '0') return { expr: s.expr.slice(0, -1) + d, done: false };
  if (bare.replace('.', '').length >= CALC_MAX_DIGITS) return s;
  if (s.expr.length >= CALC_MAX_LEN) return s;
  return { expr: s.expr + d, done: false };
}

function pressDot(s: CalcState): CalcState {
  if (s.done) return { expr: '0.', done: false };
  const num = lastNumber(s.expr);
  if (num.includes('.')) return s;
  if (s.expr.length >= CALC_MAX_LEN - 1) return s;
  // 수 없이 누르면 「0.」 로 시작한다 — 「.」 하나만 있는 수는 계산이 안 된다
  if (num === '' || num === '-') return { expr: `${s.expr}0.`, done: false };
  return { expr: `${s.expr}.`, done: false };
}

function pressOp(s: CalcState, op: CalcOp): CalcState {
  let e = s.expr;
  if (e === '' || e === '-') return s;
  if (e.endsWith('.')) e = e.slice(0, -1); // 「3.」 → 「3」
  if (endsWithOp(e)) return { expr: e.slice(0, -1) + op, done: false };
  if (e.length >= CALC_MAX_LEN) return s;
  return { expr: e + op, done: false };
}

function pressEquals(s: CalcState): CalcState {
  const r = calcEvaluate(s.expr);
  // 빈 식·오류는 그대로 둔다 — 오류 문구는 화면이 calcEvaluate 로 보인다
  if (r.kind !== 'ok') return s;
  return { expr: String(r.value), done: true };
}

function pressBack(s: CalcState): CalcState {
  const e = s.expr.slice(0, -1);
  return { expr: e === '-' ? '' : e, done: false };
}

/** 키 하나를 눌렀을 때 다음 상태 */
export function calcPress(s: CalcState, k: CalcKey): CalcState {
  switch (k) {
    case 'C':
      return CALC_EMPTY;
    case 'back':
      return pressBack(s);
    case '=':
      return pressEquals(s);
    case '.':
      return pressDot(s);
    case '+':
    case '-':
    case '*':
    case '/':
      return pressOp(s, k);
    default:
      return pressDigit(s, k);
  }
}

export type CalcValue =
  | { kind: 'empty' }
  | { kind: 'ok'; value: number }
  | { kind: 'error'; message: string };

/** 소수 여섯째 자리까지 — 0.1 + 0.2 가 0.30000000000000004 로 보이지 않게 */
function roundCalc(v: number): number {
  const r = Number(v.toFixed(6));
  return Object.is(r, -0) ? 0 : r;
}

/** 나누는 수 가운데 0 이 있나 (5 ÷ 0 · 5 ÷ 0.0) */
function hasZeroDivisor(expr: string): boolean {
  const re = /\/(-?[0-9.]+)/g;
  for (let m = re.exec(expr); m !== null; m = re.exec(expr)) {
    if (parseFloat(m[1] ?? '') === 0) return true;
  }
  return false;
}

/** 식의 답 — 끝의 기호는 없는 것으로 본다 */
export function calcEvaluate(expr: string): CalcValue {
  const e = endsWithOp(expr) ? expr.slice(0, -1) : expr;
  if (e === '' || e === '-') return { kind: 'empty' };
  const v = evalAmount(e);
  if (v === null) {
    return { kind: 'error', message: hasZeroDivisor(e) ? '0으로 나눌 수 없습니다' : '계산할 수 없는 식입니다' };
  }
  if (Math.abs(v) > CALC_MAX_ABS) return { kind: 'error', message: '수가 너무 큽니다' };
  return { kind: 'ok', value: roundCalc(v) };
}

export type CalcCompare =
  | { kind: 'none' }
  /** 개수로 못 쓰는 답 (소수·음수·너무 큼) */
  | { kind: 'bad'; message: string }
  | { kind: 'same'; value: number }
  | { kind: 'less'; value: number; gap: number }
  | { kind: 'more'; value: number; gap: number };

/** 답을 기준 수량(현재고·지금 시스템)과 견준다 */
export function calcCompare(r: CalcValue, ref: number): CalcCompare {
  if (r.kind !== 'ok') return { kind: 'none' };
  const v = r.value;
  if (!Number.isInteger(v) || v < 0) return { kind: 'bad', message: '개수는 0 이상 정수만 적을 수 있습니다' };
  if (v > CALC_QTY_MAX) return { kind: 'bad', message: `${CALC_QTY_MAX.toLocaleString('ko-KR')} 이하만 적을 수 있습니다` };
  if (v === ref) return { kind: 'same', value: v };
  return v < ref ? { kind: 'less', value: v, gap: ref - v } : { kind: 'more', value: v, gap: v - ref };
}

const OP_VIEW: Record<CalcOp, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };

/** 1234.5 → 1,234.5 · 적는 중인 「3.」 은 그대로 */
function groupNumber(n: string): string {
  if (n === '') return '';
  const neg = n.startsWith('-');
  const body = neg ? n.slice(1) : n;
  const dot = body.indexOf('.');
  const int = dot === -1 ? body : body.slice(0, dot);
  const frac = dot === -1 ? '' : body.slice(dot);
  return `${neg ? '−' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${frac}`;
}

/** 보이는 식 — 「1200*3+-」 같은 적는 모양을 「1,200 × 3 +」 로 */
export function calcExprView(expr: string): string {
  let out = '';
  let num = '';
  for (let i = 0; i < expr.length; i++) {
    const c = expr.charAt(i);
    if (isOpAt(expr, i)) {
      out += `${groupNumber(num)} ${OP_VIEW[c as CalcOp]} `;
      num = '';
    } else {
      num += c;
    }
  }
  return (out + groupNumber(num)).trimEnd();
}

/** 보이는 답 — 87 · 1,200 · 0.333333 · −3 (음수 부호는 식과 같은 − 로) */
export function calcNumberView(v: number): string {
  return v.toLocaleString('ko-KR', { maximumFractionDigits: 6 }).replace('-', '−');
}
